import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import SearchableSelect from "@/components/SearchableSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Bell, Send, Plus, Trash2, RefreshCw, Wrench, Droplet, ShieldCheck, Mail, MessageCircle, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import { whatsappShare } from "@/lib/whatsapp";
import { useAuth } from "@/context/AuthContext";

import { tr } from "@/i18n";
function todayISO(offset = 0) { const d = new Date(); d.setDate(d.getDate() + offset); return d.toISOString().slice(0, 10); }

export default function Reminders() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const canSend = hasPermission("reminders.send");
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState({ customer_id: "", reason: "Oil & filter service", due_date: todayISO(30), due_km: "", car_plate: "", car_make: "", car_model: "" });
  const { data: rows = [] } = useQuery({
    queryKey: ["reminders"],
    queryFn: () => api.get("/reminders").then(r => r.data),
    // Poll every 20s so newly-sent items flip from Pending → Sent without a
    // manual refresh, and so scheduled cron sweeps show up promptly.
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
  });
  const { data: customers = [] } = useQuery({ queryKey: ["cus"], queryFn: () => api.get("/customers").then(r => r.data) });
  const { data: settings = {} } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then(r => r.data).catch(() => ({})) });

  const scanVehicles = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/reminders/scan-vehicles");
      const c = data.created || {};
      if (data.total === 0) toast.info(`No new reminders (scanned ${data.scanned} vehicles)`);
      else toast.success(`Created ${c.apk || 0} APK + ${c.oil || 0} oil reminder(s)`);
      qc.invalidateQueries({ queryKey: ["reminders"] });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setScanning(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reminders", { ...form, customer_id: form.customer_id, due_km: form.due_km ? Number(form.due_km) : null });
      toast.success(tr("hc_reminder_scheduled"));
      setOpen(false);
      setForm({ customer_id: "", reason: "Oil & filter service", due_date: todayISO(30), due_km: "", car_plate: "", car_make: "", car_model: "" });
      qc.invalidateQueries({ queryKey: ["reminders"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sendNow = async (id) => {
    try { await api.post(`/reminders/${id}/send`); toast.success(tr("hc_email_queued")); setTimeout(() => qc.invalidateQueries({ queryKey: ["reminders"] }), 1500); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  /* Open WhatsApp Web / app with a ready-to-send reminder message. The customer
     phone is looked up from the loaded customer list (Reminder doc doesn't
     store phone). We mark the reminder "sent" so the row flips to green. */
  const sendWhatsApp = async (r) => {
    const cust = customers.find(c => c.id === r.customer_id);
    const phone = cust?.phone || "";
    if (!phone) { toast.error(tr("hc_no_phone_number_on_file")); return; }
    const veh = [r.car_make, r.car_model, r.car_plate].filter(Boolean).join(" ");
    whatsappShare({
      phone,
      garageName: settings?.name || "Garage",
      header: `Service reminder — ${r.reason}`,
      lines: [
        `Vehicle: ${veh || "your car"}`,
        `Due: ${r.due_date}${r.due_km ? ` · ${r.due_km} km` : ""}`,
      ],
      note: "Please book a slot that suits you — reply to this message or give us a call.",
    });
    // Best-effort backend mark. Falls back gracefully if endpoint is missing.
    try {
      await api.post(`/reminders/${r.id}/mark-sent`, { channel: "whatsapp" });
      qc.invalidateQueries({ queryKey: ["reminders"] });
    } catch { /* non-fatal — WhatsApp already opened */ }
  };

  const sendAllPending = async () => {
    const pendingRows = rows.filter(r => r.status === "pending" && r.customer_email);
    if (!pendingRows.length) { toast.info(tr("hc_no_pending_reminders_with_email_addres")); return; }
    if (!window.confirm(`Send ${pendingRows.length} pending reminder(s) by email now?`)) return;
    try {
      const { data } = await api.post("/reminders/send-all-pending");
      toast.success(`Queued ${data.queued} · ${data.skipped} skipped (no email)`);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["reminders"] }), 1500);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete reminder?")) return;
    try { await api.delete(`/reminders/${id}`); toast.success(tr("hc_deleted")); qc.invalidateQueries({ queryKey: ["reminders"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="reminders-page">
      <div className="page-head flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{tr("hc_retention")}</div>
          <h1 className="font-display text-4xl font-black tracking-tight">{tr("hc_service_reminders")}</h1>
          <p className="text-muted-foreground mt-2">{tr("hc_auto_nudge_customers_a_few_days_before")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <div className="flex gap-2 flex-wrap">
            {canSend && (
              <Button variant="outline" className="rounded-full" onClick={scanVehicles} disabled={scanning} data-testid="reminders-scan-vehicles">
                <RefreshCw className={`h-4 w-4 mr-2 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning..." : "Scan vehicles (APK + oil)"}
              </Button>
            )}
            {canSend && (
              <Button
                variant="outline"
                className="rounded-full text-primary border-primary/40 hover:bg-primary/10"
                onClick={sendAllPending}
                disabled={!rows.some(r => r.status === "pending" && r.customer_email)}
                data-testid="reminders-send-all-pending"
              >
                <Send className="h-4 w-4 mr-2" />
                Send all pending
                {rows.filter(r => r.status === "pending" && r.customer_email).length > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                    {rows.filter(r => r.status === "pending" && r.customer_email).length}
                  </span>
                )}
              </Button>
            )}
            {canSend && (
              <DialogTrigger asChild>
                <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="reminder-new-button"><Plus className="h-4 w-4 mr-2" /> {tr("hc_new_reminder")}</Button>
              </DialogTrigger>
            )}
          </div>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-display">{tr("hc_schedule_service_reminder")}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div className="space-y-1.5">
                <Label>{tr("hc_customer")}</Label>
                <SearchableSelect
                  value={form.customer_id}
                  onChange={(v) => setForm({ ...form, customer_id: v })}
                  options={customers.map(c => ({ value: c.id, label: c.name, secondary: c.email || "no email", disabled: !c.email }))}
                  emptyLabel="— pick —"
                  searchPlaceholder="Search customer"
                  placeholder={tr("hc_pick_customer")}
                  testId="reminder-customer"
                />
                <p className="text-[11px] text-muted-foreground">{tr("hc_only_customers_with_an_email_address_c")}</p>
              </div>
              <div className="space-y-1.5"><Label>{tr("hc_reason")}</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="reminder-reason" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>{tr("hc_due_date")}</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="reminder-date" /></div>
                <div className="space-y-1.5"><Label>{tr("hc_due_at_km")}</Label><Input type="number" value={form.due_km} onChange={(e) => setForm({ ...form, due_km: e.target.value })} placeholder="e.g. 60000" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>{tr("hc_plate")}</Label><Input value={form.car_plate} onChange={(e) => setForm({ ...form, car_plate: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{tr("hc_make")}</Label><Input value={form.car_make} onChange={(e) => setForm({ ...form, car_make: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>{tr("hc_model")}</Label><Input value={form.car_model} onChange={(e) => setForm({ ...form, car_model: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>{tr("hc_cancel")}</Button>
                <Button type="submit" className="rounded-full bg-primary" data-testid="reminder-save"><Bell className="h-4 w-4 mr-2" />{tr("hc_schedule")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>{tr("hc_type")}</TableHead><TableHead>{tr("hc_due")}</TableHead><TableHead>{tr("hc_customer")}</TableHead><TableHead>{tr("hc_vehicle")}</TableHead>
            <TableHead>{tr("hc_reason")}</TableHead><TableHead>{tr("hc_status")}</TableHead><TableHead className="text-right w-40">{tr("hc_actions")}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(r => {
              const kind = r.kind || "service";
              const Icon = kind === "apk" ? ShieldCheck : kind === "oil" ? Droplet : kind === "advice" ? Lightbulb : Wrench;
              const kindColor =
                kind === "apk" ? "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30" :
                kind === "oil" ? "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30" :
                kind === "advice" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" :
                "bg-primary/15 text-primary border-primary/30";
              const kindLabel = kind === "advice" ? tr("reminderKind_advice") : kind === "apk" ? tr("apk") : kind === "oil" ? tr("oil") : tr("service");
              return (
              <TableRow key={r.id} data-testid={`reminder-row-${r.id}`}>
                <TableCell><Badge className={`${kindColor} uppercase text-[10px]`}><Icon className="h-3 w-3 mr-1" />{kindLabel}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{r.due_date}{r.due_km ? ` · ${r.due_km} km` : ""}</TableCell>
                <TableCell>
                  <div>{r.customer_name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground">{r.customer_email || "no email"}</div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{[r.car_make, r.car_model].filter(Boolean).join(" ")} {r.car_plate}</TableCell>
                <TableCell>{r.reason}</TableCell>
                <TableCell>
                  {r.status === "sent" ? <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/15">{tr("hc_sent")}</Badge>
                   : r.status === "cancelled" ? <Badge variant="outline">{tr("hc_cancelled")}</Badge>
                   : <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/15">{tr("hc_pending")}</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {r.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={() => sendNow(r.id)}
                          disabled={!r.customer_email}
                          title={r.customer_email ? "Send email" : "Customer has no email"}
                          data-testid={`reminder-email-${r.id}`}
                        >
                          <Mail className="h-3 w-3 mr-1" />{tr("hc_email")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-emerald-700 dark:text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10"
                          onClick={() => sendWhatsApp(r)}
                          title={tr("hc_send_via_whatsapp")}
                          data-testid={`reminder-wa-${r.id}`}
                        >
                          <MessageCircle className="h-3 w-3 mr-1" />WhatsApp
                        </Button>
                      </>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => del(r.id)}><Trash2 className="h-4 w-4 text-rose-600 dark:text-rose-400" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            );})}
            {!rows.length && <TableRow><TableCell colSpan={7} className="text-center py-16 text-muted-foreground">{tr("hc_no_reminders_scheduled")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
